import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { eventsService } from '../services/eventsService';
import { favoritesService } from '../services/favoritesService';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { useToast } from '../contexts/ToastContext';
import { LoadingSpinner, Skeleton, Card, Button } from '../components/ui';
import Footer from '../components/Footer';
import { 
  ArrowLeft, 
  Heart, 
  MapPin, 
  Clock, 
  Calendar, 
  Star,
  Share2,
  Users,
  Ticket,
  Shield,
  CheckCircle,
  Music,
  Play,
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  Award,
  Globe,
  Camera,
  Video,
  CreditCard,
  Smartphone,
  Headphones,
  Volume2,
  ThumbsUp,
  MessageCircle,
  Eye,
  Bookmark,
  Send,
  MoreHorizontal,
  Facebook,
  Twitter,
  Instagram,
  Copy,
  Download
} from 'lucide-react';


interface EventDetailsProps {
  eventId?: string;
}

// Seat Button Component
interface SeatButtonProps {
  seatId: string;
  type: 'vip' | 'premium' | 'general';
  isSelected: boolean;
  isOccupied: boolean;
  onClick: (seatId: string) => void;
}

const SeatButton: React.FC<SeatButtonProps> = ({ seatId, type, isSelected, isOccupied, onClick }) => {
  const getColor = () => {
    if (isOccupied) return 'bg-red-500 cursor-not-allowed opacity-60';
    if (isSelected) return 'cursor-pointer shadow-lg';
    if (type === 'vip') return 'bg-yellow-400 hover:bg-yellow-300 cursor-pointer hover:shadow-md';
    if (type === 'premium') return 'cursor-pointer hover:shadow-md';
    return 'bg-green-400 hover:bg-green-300 cursor-pointer hover:shadow-md';
  };

  const getButtonStyle = () => {
    if (isOccupied) return { backgroundColor: '#ef4444' };
    if (isSelected) return { backgroundColor: '#f9bb10' };
    if (type === 'premium') return { backgroundColor: '#f9bb10' };
    return {};
  };

  return (
    <button
      onClick={() => !isOccupied && onClick(seatId)}
      className={`w-8 h-8 rounded-lg text-xs font-bold text-black transition-all duration-200 hover:scale-110 border-2 border-gray-300 ${getColor()}`}
      style={getButtonStyle()}
      disabled={isOccupied}
      title={isOccupied ? 'Occupied' : seatId}
    >
      {isOccupied ? '✕' : isSelected ? '✓' : ''}
    </button>
  );
};

interface TicketType {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  discount?: number;
  available: number;
  total: number;
  benefits: string[];
  color: string;
}


const EventDetails: React.FC<EventDetailsProps> = ({ eventId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { openLogin } = useAuthModal();
  const { success, error: showError } = useToast();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<string>('general');
  const [quantity, setQuantity] = useState(1);
  const [bookingType, setBookingType] = useState<'solo' | 'multi'>('solo');
  const [attendeeNames, setAttendeeNames] = useState<string[]>(['']);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [eventData, setEventData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedEvents, setRelatedEvents] = useState<any[]>([]);

  const currentEventId = eventId || id;

  // Helper function to format date (handles date ranges)
  const formatEventDate = (dateString: string) => {
    try {
      // Check if it's a date range (contains " to ")
      if (dateString && dateString.includes(' to ')) {
        const [startDate, endDate] = dateString.split(' to ');
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          return {
            from: start.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }),
            to: end.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }),
            fromDate: startDate,
            toDate: endDate
          };
        }
      }
      // Single date
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return {
          from: date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          to: null,
          fromDate: dateString,
          toDate: dateString
        };
      }
      return { from: dateString || 'TBA', to: null, fromDate: dateString || '', toDate: dateString || '' };
    } catch {
      return { from: dateString || 'TBA', to: null, fromDate: dateString || '', toDate: dateString || '' };
    }
  };

  // Load event data from API and check favorite status
  useEffect(() => {
    const loadEvent = async () => {
      if (!currentEventId) {
        setError('Event ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const eventDataFromApi = await eventsService.getEventById(currentEventId);
        
        if (!eventDataFromApi) {
          setError('Event not found');
          setIsLoading(false);
          return;
        }

        // Check if event is favorited (if user is authenticated)
        if (isAuthenticated && user) {
          try {
            const favorited = await favoritesService.isFavorited(currentEventId);
            setIsFavorited(favorited);
          } catch (err) {
            console.error('Error checking favorite status:', err);
          }
        }

        // Parse date range
        const apiEvent = eventDataFromApi as any;
        const dateInfo = formatEventDate(apiEvent.date || '');
        
        // Fetch venue data if event has venueId
        let venueGoogleMapsUrl = apiEvent.googleMapsUrl || null;
        let venueParkingInfo = null;
        if (apiEvent.venueId) {
          try {
            const { venuesApi } = await import('../services/api');
            const venueResponse = await venuesApi.getVenueById(apiEvent.venueId);
            const venue = venueResponse?.venue || venueResponse?.data || venueResponse;
            if (venue) {
              if (venue.googleMapsUrl) {
                venueGoogleMapsUrl = venue.googleMapsUrl;
              }
              if (venue.parkingInfo) {
                venueParkingInfo = venue.parkingInfo;
              }
            }
          } catch (err) {
            console.error('Error loading venue:', err);
          }
        }
        
        // Transform API event data to match component expectations
        const formattedEvent = {
          id: apiEvent.id,
          title: apiEvent.title,
          subtitle: apiEvent.description ? apiEvent.description.substring(0, 50) + '...' : '',
          artist: apiEvent.title.split(' - ')[0] || apiEvent.title,
          price: apiEvent.price || 0,
          originalPrice: apiEvent.price ? apiEvent.price * 1.2 : 0,
          discount: apiEvent.price ? Math.round((1 - apiEvent.price / (apiEvent.price * 1.2)) * 100) : 0,
          date: dateInfo.from || apiEvent.date || 'TBA',
          dateInfo: dateInfo, // Store full date info for From/To dates
          time: apiEvent.time || '20:00',
          venue: apiEvent.venue || 'TBA',
          venueName: apiEvent.venue || 'TBA',
          location: apiEvent.location || apiEvent.venue || 'TBA',
          coordinates: { lat: 31.9454, lng: 35.9284 }, // Default coordinates
          image: apiEvent.image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop',
          coverImage: apiEvent.image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200&h=800&fit=crop',
          gallery: apiEvent.gallery && apiEvent.gallery.length > 0 
            ? apiEvent.gallery 
            : [
                apiEvent.image || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop',
                'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800&h=600&fit=crop',
                'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop',
                'https://images.unsplash.com/photo-1551913902-c92207136625?w=800&h=600&fit=crop'
              ],
          videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
          rating: 4.5 + Math.random() * 0.5, // Random rating between 4.5-5.0
          totalReviews: Math.floor(Math.random() * 1000) + 100,
          capacity: apiEvent.capacity || 1000,
          description: apiEvent.description || 'Join us for an unforgettable event experience.',
          highlights: apiEvent.tags?.slice(0, 5) || ['Featured Event', 'Popular', 'Limited Tickets'],
          organizer: {
            name: 'Event Organizer',
            verified: true,
            events: 50,
            rating: 4.8
          },
          category: apiEvent.category || 'General',
          genre: apiEvent.category || 'General',
          dresscode: apiEvent.dresscode || 'Smart Casual',
          duration: apiEvent.duration || '2.5 hours',
          tags: apiEvent.tags || [],
          googleMapsUrl: venueGoogleMapsUrl,
          venueParkingInfo: venueParkingInfo,
          sections: apiEvent.sections || [],
          socialMedia: {
            instagram: '@shufeshow',
            twitter: '@shufeshow',
            facebook: 'ShufeShow'
          }
        };

        setEventData(formattedEvent);
      } catch (err: any) {
        console.error('Error loading event:', err);
        setError(err.message || 'Failed to load event');
      } finally {
        setIsLoading(false);
      }
    };

    loadEvent();
  }, [currentEventId, isAuthenticated, user]);

  // Handle favorite toggle
  const handleToggleFavorite = async () => {
    if (!isAuthenticated || !user) {
      // Show login prompt or navigate to login
      navigate('/');
      return;
    }

    if (!currentEventId) return;

    try {
      setIsTogglingFavorite(true);
      const newFavoriteStatus = await favoritesService.toggleFavorite(currentEventId, isFavorited);
      setIsFavorited(newFavoriteStatus);
      if (newFavoriteStatus) {
        success('Added to favorites');
      } else {
        success('Removed from favorites');
      }
    } catch (err: any) {
      console.error('Error toggling favorite:', err);
      showError(err.message || 'Failed to update favorite');
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  // Load related events (other published events in the same category)
  useEffect(() => {
    const loadRelatedEvents = async () => {
      if (!eventData) return;
      
      try {
        const allEvents = await eventsService.getPublishedEvents();
        // Filter out current event and get events from same category
        const related = allEvents
          .filter(e => e.id !== eventData.id && e.category === eventData.category)
          .slice(0, 3)
          .map(e => {
            const dateInfo = formatEventDate(e.date || '');
            return {
              id: e.id,
              title: e.title,
              date: dateInfo.from || e.date || 'N/A',
              price: e.price,
              image: e.image,
              venue: e.venue
            };
          });
        setRelatedEvents(related);
      } catch (err) {
        console.error('Error loading related events:', err);
      }
    };

    if (eventData) {
      loadRelatedEvents();
    }
  }, [eventData]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'var(--liquid-gradient-bg)'}}>
        <LoadingSpinner size="lg" text="Loading event details..." />
      </div>
    );
  }

  // Error state
  if (error || !eventData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: 'var(--liquid-gradient-bg)'}}>
        <Card variant="glass" className="p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Event Not Found</h2>
          <p className="mb-6 opacity-70" style={{color: 'var(--liquid-text-muted)'}}>
            {error || 'The event you are looking for does not exist.'}
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/')}
          >
            Go Back Home
          </Button>
        </Card>
      </div>
    );
  }

  // Generate ticket types from event sections
  const ticketTypes: TicketType[] = eventData && eventData.sections && eventData.sections.length > 0
    ? eventData.sections.map((section: any, index: number) => {
        const sectionId = section.id || `section-${index}`;
        const sectionPrice = section.price || 0;
        const sectionSeats = section.seats || 0;
        const ticketsSold = eventData.attendees || 0;
        
        // Calculate available seats for this section (rough estimate based on total tickets sold)
        const totalCapacity = eventData.capacity || 1000;
        const sectionCapacityRatio = totalCapacity > 0 ? sectionSeats / totalCapacity : 0;
        const estimatedSoldForSection = Math.floor(ticketsSold * sectionCapacityRatio);
        const available = Math.max(0, sectionSeats - estimatedSoldForSection);

        // Use section description if available, otherwise use benefits array, otherwise use defaults
        let sectionBenefits: string[] = [];
        if (section.description && section.description.trim()) {
          // Split description by commas and clean up
          sectionBenefits = section.description
            .split(',')
            .map((b: string) => b.trim())
            .filter((b: string) => b.length > 0);
        } else if (section.benefits && Array.isArray(section.benefits) && section.benefits.length > 0) {
          sectionBenefits = section.benefits;
        } else {
          sectionBenefits = [
            `${section.name} access`,
            'Access to venue facilities',
            'Merchandise discount',
            'Digital souvenir photo'
          ];
        }

        return {
          id: sectionId,
          name: section.name || 'General Admission',
          price: sectionPrice,
          originalPrice: sectionPrice * 1.2, // 20% markup for original price
          discount: Math.round((1 - sectionPrice / (sectionPrice * 1.2)) * 100),
          available: available,
          total: sectionSeats,
          benefits: sectionBenefits,
          color: index === 0 ? 'from-blue-500 to-cyan-500' : 
                 index === 1 ? 'from-yellow-500 to-orange-500' : 
                 'from-purple-500 to-pink-500'
        };
      })
    : eventData ? [
        // Fallback to default ticket types if no sections
        {
          id: 'general',
          name: 'General Admission',
          price: eventData.price || 100,
          originalPrice: (eventData.price || 100) * 1.2,
          discount: 17,
          available: Math.max(0, (eventData.capacity || 1000) - (eventData.attendees || 0)),
          total: eventData.capacity || 1000,
          benefits: [
            'General admission access',
            'Access to venue facilities',
            'Merchandise discount',
            'Digital souvenir photo'
          ],
          color: 'from-blue-500 to-cyan-500'
        }
      ] : [];

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const text = `Check out ${eventData.title} - ${eventData.date}`;
    
    try {
      switch (platform) {
        case 'facebook':
          window.open(`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
          success('Opening Facebook...');
          break;
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
          success('Opening Twitter...');
          break;
        case 'copy':
          await navigator.clipboard.writeText(url);
          success('Link copied to clipboard!');
          break;
      }
    } catch (err) {
      showError('Failed to share event');
    }
    setShowShareModal(false);
  };

  const handleBookTickets = () => {
    if (!isAuthenticated) {
      openLogin();
      return;
    }
    const selectedTicketData = ticketTypes.find(t => t.id === selectedTicket);
    if (selectedTicketData) {
      const finalQuantity = bookingType === 'solo' ? 1 : quantity;
      const finalAttendeeNames = bookingType === 'solo' ? [] : attendeeNames.filter(name => name.trim());
      
      navigate('/checkout', {
        state: {
          event: eventData,
          ticket: selectedTicketData,
          quantity: finalQuantity,
          bookingType,
          attendeeNames: finalAttendeeNames
        }
      });
    }
  };

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>

      {/* Hero Section */}
      <div className="relative pt-16 z-10">
        {/* Cover Image */}
        <div className="relative h-96 md:h-[500px] overflow-hidden">
          <img 
            src={eventData.coverImage} 
            alt={eventData.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          
          {/* Hero Controls */}
          <div className="absolute top-6 left-4 right-4 flex items-center justify-between z-20">
            <button 
              onClick={() => navigate(-1)}
              className="w-12 h-12 glass-button rounded-full flex items-center justify-center  glow hover:scale-110 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
            >
              <ArrowLeft size={20} />
            </button>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowShareModal(true)}
                className="w-12 h-12 glass-button rounded-full flex items-center justify-center  glow hover:scale-110 transition-transform" style={{color: 'var(--liquid-text-primary)'}}
              >
                <Share2 size={20} />
              </button>
              <button 
                onClick={handleToggleFavorite}
                disabled={isTogglingFavorite}
                className="w-12 h-12 glass-button rounded-full flex items-center justify-center glow hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed" 
                style={{color: 'var(--liquid-text-primary)'}}
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart 
                  size={20} 
                  className={isFavorited ? 'text-red-400 fill-current' : ''}
                />
              </button>
            </div>
          </div>


          {/* Hero Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="max-w-4xl">
              {/* Event Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-gradient-to-r from-red-500 to-pink-500  text-xs font-bold rounded-full" style={{color: 'var(--liquid-text-primary)'}}>
                  TRENDING
                </span>
                <span className="px-3 py-1 glass rounded-full  text-xs font-medium" style={{color: 'var(--liquid-text-primary)'}}>
                  {eventData.genre}
                </span>
              </div>

              <h1 className="text-3xl md:text-5xl font-bold  mb-2" style={{color: 'var(--liquid-text-primary)'}}>
                {eventData.title}
              </h1>
              <p className="text-xl /80 mb-4" style={{color: 'var(--liquid-text-primary)'}}>{eventData.subtitle}</p>
              
              <div className="flex flex-wrap items-center gap-6 /90" style={{color: 'var(--liquid-text-primary)'}}>
                <div className="flex items-center space-x-2">
                  <Calendar size={18} />
                  <span className="font-medium">{eventData.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock size={18} />
                  <span className="font-medium">{eventData.time}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin size={18} />
                  <span className="font-medium">{eventData.venue}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Event Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Navigation Tabs */}
              <div className="glass-card rounded-2xl p-2">
                <div className="flex space-x-1">
                  {[
                    { id: 'overview', label: 'Overview', icon: Eye },
                    { id: 'location', label: 'Location', icon: MapPin }
                  ].map((tab) => {
                    const IconComponent = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-xl transition-all ${
                          activeTab === tab.id
                            ? 'bg-white/20 text-white'
                            : 'text-white/70 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        <IconComponent size={18} />
                        <span className="font-medium">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  {/* Event Description */}
                  <div className="glass-card rounded-3xl p-8">
                    <h2 className="text-2xl font-bold  mb-6 flex items-center" style={{color: 'var(--liquid-text-primary)'}}>
                      <Music size={24} className="mr-3" style={{color: '#f9bb10'}} />
                      About This Event
                    </h2>
                    <p className="/80 leading-relaxed mb-6" style={{color: 'var(--liquid-text-primary)'}}>
                      {eventData.description}
                    </p>
                    
                    {/* Event Highlights */}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold  mb-4" style={{color: 'var(--liquid-text-primary)'}}>Event Highlights</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {eventData.highlights.map((highlight: string, index: number) => (
                          <div key={index} className="flex items-center space-x-3">
                            <CheckCircle size={16} className="text-green-400 flex-shrink-0" />
                            <span className="/80" style={{color: 'var(--liquid-text-primary)'}}>{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                                      {/* Event Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 glass rounded-2xl">
                      <Calendar size={24} className="mx-auto mb-2 text-blue-400" />
                      <div className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>From Date</div>
                      <div className="font-semibold text-xs" style={{color: 'var(--liquid-text-primary)'}}>
                        {eventData.dateInfo?.from || 'N/A'}
                      </div>
                    </div>
                    {eventData.dateInfo?.to && eventData.dateInfo.to !== eventData.dateInfo.from && (
                      <div className="text-center p-4 glass rounded-2xl">
                        <Calendar size={24} className="mx-auto mb-2 text-green-400" />
                        <div className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>To Date</div>
                        <div className="font-semibold text-xs" style={{color: 'var(--liquid-text-primary)'}}>
                          {eventData.dateInfo.to}
                        </div>
                      </div>
                    )}
                    <div className="text-center p-4 glass rounded-2xl">
                      <MapPin size={24} className="mx-auto mb-2 text-purple-400" />
                      <div className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>Venue</div>
                      <div className="font-semibold text-xs" style={{color: 'var(--liquid-text-primary)'}}>{eventData.venueName || 'N/A'}</div>
                    </div>
                    <div className="text-center p-4 glass rounded-2xl">
                      <MapPin size={24} className="mx-auto mb-2 text-yellow-400" />
                      <div className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>Location</div>
                      <div className="font-semibold text-xs" style={{color: 'var(--liquid-text-primary)'}}>{eventData.location || 'N/A'}</div>
                    </div>
                    <div className="text-center p-4 glass rounded-2xl">
                      <Clock size={24} className="mx-auto mb-2 text-blue-400" />
                      <div className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>Duration</div>
                      <div className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{eventData.duration || 'N/A'}</div>
                    </div>
                    <div className="text-center p-4 glass rounded-2xl">
                      <Award size={24} className="mx-auto mb-2 text-yellow-400" />
                      <div className="text-sm opacity-60" style={{color: 'var(--liquid-text-primary)'}}>Dress Code</div>
                      <div className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{eventData.dresscode || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Select Tickets Section - Moved here with improved UI */}
                <div className="glass-card rounded-3xl p-8 mt-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center" style={{color: 'var(--liquid-text-primary)'}}>
                    <Ticket size={24} className="mr-3" style={{color: '#f9bb10'}} />
                    Select Tickets
                  </h2>

                  {/* Booking Type Selector */}
                  <div className="glass rounded-2xl p-2 mb-6">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setBookingType('solo');
                          setQuantity(1);
                          setAttendeeNames(['']);
                        }}
                        className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                          bookingType === 'solo'
                            ? 'bg-white/20 text-white'
                            : 'text-white/70 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        Solo Booking
                      </button>
                      <button
                        onClick={() => {
                          setBookingType('multi');
                          setQuantity(1);
                          setAttendeeNames(['']);
                        }}
                        className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                          bookingType === 'multi'
                            ? 'bg-white/20 text-white'
                            : 'text-white/70 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        Multi-Booking
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    {ticketTypes.map((ticket) => (
                      <div
                        key={ticket.id}
                        onClick={() => setSelectedTicket(ticket.id)}
                        className={`glass rounded-2xl p-6 cursor-pointer transition-all duration-300 border-2 ${
                          selectedTicket === ticket.id
                            ? 'border-yellow-400 shadow-lg shadow-yellow-400/20 scale-[1.02]'
                            : 'border-transparent hover:border-white/20 hover:scale-[1.01]'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>
                                {ticket.name}
                              </h4>
                              {ticket.discount && ticket.discount > 0 && (
                                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">
                                  {ticket.discount}% OFF
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-3 mb-3">
                              <span className="text-2xl font-bold" style={{color: '#f9bb10'}}>
                                ${ticket.price}
                              </span>
                              {ticket.originalPrice && ticket.originalPrice > ticket.price && (
                                <span className="text-lg line-through opacity-60" style={{color: 'var(--liquid-text-primary)'}}>
                                  ${ticket.originalPrice}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center space-x-4 text-sm mb-4" style={{color: 'var(--liquid-text-muted)'}}>
                              <span>{ticket.available} available</span>
                              <span>•</span>
                              <span>{ticket.total} total</span>
                            </div>

                            <div className="space-y-2">
                              {ticket.benefits.map((benefit: string, idx: number) => (
                                <div key={idx} className="flex items-center space-x-2 text-sm" style={{color: 'var(--liquid-text-primary)'}}>
                                  <CheckCircle size={16} className="text-green-400 flex-shrink-0" />
                                  <span>{benefit}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="ml-4">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              selectedTicket === ticket.id
                                ? 'border-yellow-400 bg-yellow-400'
                                : 'border-white/30'
                            }`}>
                              {selectedTicket === ticket.id && (
                                <div className="w-3 h-3 rounded-full bg-white"></div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Solo Booking Panel */}
                  {bookingType === 'solo' && (
                    <>
                      {/* Total Price */}
                      <div className="glass rounded-2xl p-6 mb-6">
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-medium" style={{color: 'var(--liquid-text-primary)'}}>
                            Total (1 ticket)
                          </span>
                          <span className="text-3xl font-bold" style={{color: '#f9bb10'}}>
                            ${ticketTypes.find(t => t.id === selectedTicket)?.price || 0}
                          </span>
                        </div>
                      </div>

                      {/* Book Tickets Button */}
                      <button
                        onClick={handleBookTickets}
                        className="w-full liquid-glass-button px-8 py-4 rounded-2xl text-white font-bold text-lg liquid-glow hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                      >
                        <Ticket size={20} />
                        <span>Book Ticket</span>
                      </button>
                    </>
                  )}

                  {/* Multi-Booking Panel */}
                  {bookingType === 'multi' && (
                    <>
                      {/* Quantity Selector */}
                      <div className="glass rounded-2xl p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <label className="text-lg font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                            Quantity
                          </label>
                          <div className="flex items-center space-x-4">
                            <button
                              onClick={() => {
                                const newQty = Math.max(1, quantity - 1);
                                setQuantity(newQty);
                                setAttendeeNames(Array(newQty).fill('').map((_, i) => attendeeNames[i] || ''));
                              }}
                              className="w-10 h-10 rounded-full glass-button flex items-center justify-center font-bold hover:scale-110 transition-transform"
                              style={{color: 'var(--liquid-text-primary)'}}
                            >
                              -
                            </button>
                            <span className="text-xl font-bold w-12 text-center" style={{color: 'var(--liquid-text-primary)'}}>
                              {quantity}
                            </span>
                            <button
                              onClick={() => {
                                const newQty = quantity + 1;
                                setQuantity(newQty);
                                setAttendeeNames([...attendeeNames, ...Array(newQty - attendeeNames.length).fill('')]);
                              }}
                              className="w-10 h-10 rounded-full glass-button flex items-center justify-center font-bold hover:scale-110 transition-transform"
                              style={{color: 'var(--liquid-text-primary)'}}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        {/* Attendee Name Fields */}
                        <div className="space-y-3">
                          <label className="text-sm font-medium block" style={{color: 'var(--liquid-text-primary)'}}>
                            Attendee Names
                          </label>
                          {Array.from({ length: quantity }).map((_, index) => (
                            <input
                              key={index}
                              type="text"
                              value={attendeeNames[index] || ''}
                              onChange={(e) => {
                                const newNames = [...attendeeNames];
                                newNames[index] = e.target.value;
                                setAttendeeNames(newNames);
                              }}
                              className="w-full px-4 py-3 glass-input rounded-xl bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus-glass border border-white/20"
                              placeholder={`Attendee ${index + 1} Full Name`}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Total Price */}
                      <div className="glass rounded-2xl p-6 mb-6">
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-medium" style={{color: 'var(--liquid-text-primary)'}}>
                            Total ({quantity} {quantity === 1 ? 'ticket' : 'tickets'})
                          </span>
                          <span className="text-3xl font-bold" style={{color: '#f9bb10'}}>
                            ${(ticketTypes.find(t => t.id === selectedTicket)?.price || 0) * quantity}
                          </span>
                        </div>
                      </div>

                      {/* Book Tickets Button */}
                      <button
                        onClick={handleBookTickets}
                        className="w-full liquid-glass-button px-8 py-4 rounded-2xl text-white font-bold text-lg liquid-glow hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                      >
                        <Ticket size={20} />
                        <span>Book Tickets</span>
                      </button>
                    </>
                  )}

                  {/* Security Features */}
                  <div className="mt-6 flex items-center justify-center space-x-6 text-sm" style={{color: 'var(--liquid-text-muted)'}}>
                    <div className="flex items-center space-x-2">
                      <Shield size={16} className="text-green-400" />
                      <span>Secure Payment</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Smartphone size={16} className="text-blue-400" />
                      <span>Mobile Tickets</span>
                    </div>
                  </div>
                </div>

                {/* Organizer Info */}
                  <div className="glass-card rounded-3xl p-6">
                    <h3 className="text-xl font-bold  mb-4" style={{color: 'var(--liquid-text-primary)'}}>Event Organizer</h3>
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 glass-strong rounded-full flex items-center justify-center">
                        <Users size={24} className="" style={{color: 'var(--liquid-text-primary)'}} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-lg font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{eventData.organizer.name}</h4>
                          {eventData.organizer.verified && (
                            <CheckCircle size={18} className="text-blue-400" />
                          )}
                        </div>
                        <div className="flex items-center space-x-4 /70 text-sm" style={{color: 'var(--liquid-text-primary)'}}>
                          <span>{eventData.organizer.events} events organized</span>
                          <div className="flex items-center space-x-1">
                            <Star size={14} className="text-yellow-400 fill-current" />
                            <span>{eventData.organizer.rating} rating</span>
                          </div>
                        </div>
                      </div>
                      <button className="glass-button px-4 py-2 rounded-xl  font-medium hover:scale-105 transition-transform" style={{color: 'var(--liquid-text-primary)'}}>
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'location' && (
                <div className="glass-card rounded-3xl p-8">
                  <h2 className="text-2xl font-bold mb-6" style={{color: 'var(--liquid-text-primary)'}}>Venue & Location</h2>
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <MapPin size={24} className="text-blue-400" />
                        <h3 className="text-lg font-semibold" style={{color: 'var(--liquid-text-primary)'}}>{eventData.venue || 'TBA'}</h3>
                      </div>
                      {eventData.location && (
                        <p className="opacity-70 text-lg" style={{color: 'var(--liquid-text-primary)'}}>{eventData.location}</p>
                      )}
                    </div>
                    
                    {/* Google Maps */}
                    {eventData.googleMapsUrl ? (
                      <div className="relative h-64 bg-gray-800 rounded-2xl overflow-hidden">
                        <iframe
                          src={eventData.googleMapsUrl}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen={true}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Google Maps Location"
                        ></iframe>
                      </div>
                    ) : (
                      <div className="h-64 glass rounded-2xl flex items-center justify-center">
                        <div className="text-center">
                          <MapPin size={48} className="mx-auto mb-4 opacity-60" style={{color: 'var(--liquid-text-primary)'}} />
                          <p className="opacity-70" style={{color: 'var(--liquid-text-primary)'}}>Map location not available</p>
                        </div>
                      </div>
                    )}

                    {/* Parking Information */}
                    {eventData.venueParkingInfo && eventData.venueParkingInfo.trim() && (
                      <div>
                        <h4 className="text-lg font-semibold mb-4" style={{color: 'var(--liquid-text-primary)'}}>Parking</h4>
                        <div className="glass rounded-2xl p-4">
                          <p className="opacity-80 leading-relaxed" style={{color: 'var(--liquid-text-primary)'}}>
                            {eventData.venueParkingInfo}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Related Events */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Related Events */}
                <div className="glass-card rounded-3xl p-6">
                  <h3 className="text-xl font-bold  mb-4" style={{color: 'var(--liquid-text-primary)'}}>You Might Also Like</h3>
                  <div className="space-y-4">
                    {relatedEvents.map((relatedEvent) => (
                      <div 
                        key={relatedEvent.id}
                        className="flex space-x-4 p-3 glass rounded-2xl hover:scale-105 transition-transform cursor-pointer"
                        onClick={() => navigate(`/event/${relatedEvent.id}`)}
                      >
                        <img 
                          src={relatedEvent.image} 
                          alt={relatedEvent.title}
                          className="w-16 h-16 rounded-xl object-cover"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium  text-sm line-clamp-2" style={{color: 'var(--liquid-text-primary)'}}>{relatedEvent.title}</h4>
                          <p className="/60 text-xs" style={{color: 'var(--liquid-text-primary)'}}>{relatedEvent.date}</p>
                          <p className="/60 text-xs" style={{color: 'var(--liquid-text-primary)'}}>{relatedEvent.venue}</p>
                          <p className="font-semibold text-sm" style={{color: 'var(--liquid-text-primary)'}}>${relatedEvent.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold" style={{color: 'var(--liquid-text-primary)'}}>Share Event</h3>
              <button 
                onClick={() => setShowShareModal(false)}
                className="w-8 h-8 glass-button rounded-full flex items-center justify-center" style={{color: 'var(--liquid-text-primary)'}}
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <button 
                onClick={() => handleShare('facebook')}
                className="flex flex-col items-center p-4 glass rounded-2xl hover:scale-105 transition-transform"
              >
                <Facebook size={24} className="text-blue-500 mb-2" />
                <span className="text-sm" style={{color: 'var(--liquid-text-primary)'}}>Facebook</span>
              </button>
              <button 
                onClick={() => handleShare('twitter')}
                className="flex flex-col items-center p-4 glass rounded-2xl hover:scale-105 transition-transform"
              >
                <Twitter size={24} className="text-blue-400 mb-2" />
                <span className="text-sm" style={{color: 'var(--liquid-text-primary)'}}>Twitter</span>
              </button>
              <button 
                onClick={() => handleShare('copy')}
                className="flex flex-col items-center p-4 glass rounded-2xl hover:scale-105 transition-transform"
              >
                <Copy size={24} className="text-gray-400 mb-2" />
                <span className="text-sm" style={{color: 'var(--liquid-text-primary)'}}>Copy Link</span>
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Footer */}
      <Footer />

    </div>
  );
};

export default EventDetails;
